import store from '../../config/configureStore';
import { incidentAllOffensesSchema } from 'simpl-schema-validation/schema';
import { generateHandleEditIncident } from 'simpl-schema-validation/helpers/generateHandleEdit';

// DE-24 grouping: one code set per victim (person OR place/business),
// mirroring the generator's collapse by ptsIncPersonID / ptsIncPlaceID
// (xmlGenerator/groupAModel.js, which buckets place victims into
// placeVictimBuckets and folds them into the same victimInstances mechanism
// as person victims). Victims with NEITHER id (e.g. society/public victims)
// cannot carry a person-offense conflict and are skipped.
//
// Person and place ids are independent id spaces, so the place bucket is
// keyed with a "place:" prefix - a bare numeric key would let a person and a
// place that happen to share the same id merge into one bogus victim.
//
// Shared by every consumer of `incidentAllOffensesSchema` so the grouping used
// to evaluate error 478 never drifts between call sites - see rules 111/112
// history, where duplicated offense-pair logic silently diverged.
export const buildUcrCodesByVictim = (incVictims, offenses) => {
  const codesByVictim = new Map();
  (Array.isArray(incVictims) ? incVictims : []).forEach((incVictim) => {
    const details = incVictim && incVictim.incVictimDetails;
    const personId = details && details.ptsIncPersonId;
    const placeId = details && details.ptsIncPlaceId;
    if (!personId && !placeId) return;
    const key = personId ? personId : `place:${placeId}`;
    const offense = (Array.isArray(offenses) ? offenses : []).find(
      (o) =>
        o.offenseDetails.ptsOffenseId === details.ptsOffenseId &&
        o.offenseDetails.values.excludeOffense === false &&
        o.offenseDetails.values?.statuteDetails?.FBICode !== '0'
    );
    const code = offense?.offenseDetails?.values?.statuteDetails?.FBICode;
    if (!code) return;
    if (!codesByVictim.has(key)) {
      codesByVictim.set(key, { personId: personId ? personId : placeId, codes: [] });
    }
    const codes = codesByVictim.get(key).codes;
    if (!codes.includes(code)) codes.push(code);
  });
  return Array.from(codesByVictim.values());
};

export const validateAllOffenses = () => {
  const currentState = store.store.getState().incident;
  const { offenses, incVictims, turnOfValidation } = currentState;
  if (turnOfValidation) return [];

  const ucrCodes = [];
  const biasMotivationCodes = [];
  const criminalActivityCodes = [];
  const offenderSuspectedOfUsingCodes = [];
  const forceCategoryCodes = [];

  offenses.forEach((offense) => {
    if (
      offense.offenseDetails.values.statuteDetails?.FBICode &&
      offense.offenseDetails.values.excludeOffense === false &&
      offense.offenseDetails.values?.statuteDetails?.FBICode !== '0'
    ) {
      ucrCodes.push(offense.offenseDetails.values.statuteDetails?.FBICode);
      biasMotivationCodes.push(offense.offenseDetails.values.biasMotivation);
      criminalActivityCodes.push(offense.offenseDetails.values.criminalActivity);
      offenderSuspectedOfUsingCodes.push(offense.offenseDetails.values.offenderSuspectedOfUsing);
      forceCategoryCodes.push(offense.offenseDetails.values.forceCategory);
    }
  });

  const ucrCodesByVictim = buildUcrCodesByVictim(incVictims, offenses);

  let validationErrors = [];
  let allOffensesValidationContext = incidentAllOffensesSchema().newContext();
  let result = allOffensesValidationContext.validate(
    {
      ucrCodes,
      ucrCodesByVictim,
      biasMotivationCodes,
      criminalActivityCodes,
      offenderSuspectedOfUsingCodes,
      forceCategoryCodes,
    },
    {
      keys: ['ucrCodes'],
    }
  );

  if (!result) {
    allOffensesValidationContext.validationErrors().forEach((error) => {
      validationErrors.push({
        id: currentState.ptsIncidentId,
        idType: 'ptsIncidentId',
        errType: error.type,
        field: error.name,
        name: 'Offenses',
        screen: 'Incident Offense',
        stepper: 2,
        message: allOffensesValidationContext.keyErrorMessage(error.name),
        ptsIncidentId: currentState.ptsIncidentId,
        ptsOffenseId: currentState.currentOffense,
        handleEdit: generateHandleEditIncident(currentState.ptsIncidentId, 2),
        identifier: `${currentState.ptsIncidentId}-${error.type.split('-').pop()}-${
          error.name
        }-Offense-${currentState.currentOffense}`,
      });
    });
  }

  return validationErrors;
};
